import { ReactNode } from "react";
import Link, { LinkProps } from "next/link";

export enum ButtonTypes {
  Primary = "primary",
  Secondary = "secondary",
  Tertiary = "tertiary",
}

export enum ButtonSizes {
  Small = "small",
  Medium = "medium",
  Large = "large",
}

export enum IconTypes {
  None = "none",
  Left = "left",
  Right = "right",
  Only = "only",
}

type BaseStyledButtonProps = {
  buttonType: ButtonTypes;
  size: ButtonSizes;
  children: ReactNode;
  iconType?: IconTypes;
  className?: string;
};

type AsButtonProps = BaseStyledButtonProps &
  React.ButtonHTMLAttributes<HTMLButtonElement> & {
    as?: "button";
  };

type AsLinkProps = BaseStyledButtonProps &
  LinkProps &
  React.AnchorHTMLAttributes<HTMLAnchorElement> & { as: "link" };

type StyledButtonProps = AsButtonProps | AsLinkProps;

const baseStyles =
  "rounded-full justify-center items-center gap-1 inline-flex text-base font-semibold font-roobert disabled:opacity-20";
const onlyTextStyles = "px-4 py-3.5";
const withLeftIconStyles = "pl-3 pr-4 py-3.5";
const withRightIconStyles = "pl-4 pr-3 py-3.5";
const onlyIconSmallStyles = "p-1 w-8";
const onlyIconMediumStyles = "p-2 w-10";
const onlyIconLargeStyles = "p-3 w-12";

const smallStyles = "h-8";
const mediumStyles = "h-10";
const largeStyles = "h-12";

const primaryStyles = "bg-BG-60 hover:bg-BG-70 active:bg-BG-80 text-white";
const secondaryStyles =
  "border border-BG-60 hover:bg-BG-30 active:bg-BG-40 text-BG-60 hover:text-BG-70 active:text-BG-80";
const tertiaryStyles =
  "border border-tab-border text-BG-60 hover:bg-neutral-100 active:bg-tab-bg-press";

export const StyledButton = ({
  buttonType,
  size,
  children,
  className,
  iconType = IconTypes.None,
  ...props
}: StyledButtonProps) => {
  let buttonStyles = baseStyles;

  switch (buttonType) {
    case ButtonTypes.Primary:
      buttonStyles = `${buttonStyles} ${primaryStyles}`;
      break;
    case ButtonTypes.Secondary:
      buttonStyles = `${buttonStyles} ${secondaryStyles}`;
      break;
    case ButtonTypes.Tertiary:
      buttonStyles = `${buttonStyles} ${tertiaryStyles}`;
      break;
  }

  const isIconOnly = iconType === IconTypes.Only;

  switch (size) {
    case ButtonSizes.Small:
      buttonStyles = `${buttonStyles} ${smallStyles} ${
        isIconOnly ? onlyIconSmallStyles : ""
      }`;
      break;
    case ButtonSizes.Medium:
      buttonStyles = `${buttonStyles} ${mediumStyles} ${
        isIconOnly ? onlyIconMediumStyles : ""
      }`;
      break;
    case ButtonSizes.Large:
      buttonStyles = `${buttonStyles} ${largeStyles} ${
        isIconOnly ? onlyIconLargeStyles : ""
      }`;
      break;
  }

  switch (iconType) {
    case IconTypes.Left:
      buttonStyles = `${buttonStyles} ${withLeftIconStyles}`;
      break;
    case IconTypes.Right:
      buttonStyles = `${buttonStyles} ${withRightIconStyles}`;
      break;
    case IconTypes.None:
      buttonStyles = `${buttonStyles} ${onlyTextStyles}`;
      break;
  }

  if (props.as === "link") {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { as: _as, ...rest } = props;

    return (
      <Link className={`${buttonStyles} ${className}`} {...rest}>
        {children}
      </Link>
    );
  }

  return (
    <button className={`${buttonStyles} ${className}`} {...props}>
      {children}
    </button>
  );
};
